import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import PageTitle from 'src/components/PageTitle';
import PageTitleWrapper from 'src/components/PageTitleWrapper';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControl, IconButton, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { blue, red } from '@mui/material/colors';
import dayjs from 'dayjs';
import { authenticationService } from 'src/services/authentication.service';
import { deleteEntry, get } from 'src/services';
import { useSnackbar } from 'src/components/snackbar/useSnackbar';
import { hasPermission } from 'src/components/Permissions/hasPermission';
import AddTax from './AddTax';
import EditTax from './EditTax';

interface TaxData {
  id: number;
  taxType: string;
  value: number;
  calculationOn: string;
  calculationOrder: number;
  taxCategory: string;
  accountName: string;
  percentage: boolean;
  addition: boolean;
  status: string;
  createdOn: string;
  updatedOn?: string;
}

const Tax = () => {
  const { showSnackbar } = useSnackbar();
  const { vendorId } = authenticationService.getVendorFranchiseIds();
  
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [tableData, setTableData] = useState<TaxData[]>([]);
  const [editedTax, setEditedTax] = useState<TaxData | null>(null);
  const [deleteConfirmationId, setDeleteConfirmationId] = useState<number | null>(null);
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
  const [loading, setLoading] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [taxTypeFilter, setTaxTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [calculationOnFilter, setCalculationOnFilter] = useState('');
  
  const [taxTypeOptions, setTaxTypeOptions] = useState<string[]>([]);
  const [statusOptions, setStatusOptions] = useState<string[]>([]);
  const [calculationOnOptions, setCalculationOnOptions] = useState<string[]>([]);

  const fetchTaxData = async () => {
    setLoading(true);
    try {
      const response = await get(`${process.env.REACT_APP_BASE_API_URL}/api/tax/vendor/${vendorId}/getAllTaxesByVendor`);
      if (response && response.data) {
        setTableData(response.data);
      } else if (Array.isArray(response)) {
        setTableData(response);
      } else {
        setTableData([]);
      }
    } catch (error) {
      console.error('Error fetching tax data:', error);
      showSnackbar('Error fetching tax data', 'error');
      setTableData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const [taxTypesRes, statusesRes, calculationOnRes] = await Promise.all([
        get(`${process.env.REACT_APP_BASE_API_URL}/api/tax/vendor/${vendorId}/distinct-tax-types`),
        get(`${process.env.REACT_APP_BASE_API_URL}/api/tax/vendor/${vendorId}/distinct-statuses`),
        get(`${process.env.REACT_APP_BASE_API_URL}/api/tax/vendor/${vendorId}/distinct-calculation-on`)
      ]);
      
      setTaxTypeOptions(taxTypesRes?.data || taxTypesRes || []);
      setStatusOptions(statusesRes?.data || statusesRes || []);
      setCalculationOnOptions(calculationOnRes?.data || calculationOnRes || []);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  useEffect(() => {
    fetchTaxData();
    fetchFilterOptions();
  }, []);

  const handleOpenAddDialog = () => {
    setOpenAddDialog(true);
  };

  const getFilteredRows = () => {
    let filteredRows = tableData;

    if (searchQuery) {
      filteredRows = filteredRows.filter((row) =>
        Object.values(row).some(
          (value) => value && value.toString().toLowerCase().includes(searchQuery.toLowerCase())
        )
      );
    }

    if (taxTypeFilter) {
      filteredRows = filteredRows.filter(
        (row) => row.taxType?.toLowerCase() === taxTypeFilter.toLowerCase()
      );
    }

    if (statusFilter) {
      filteredRows = filteredRows.filter(
        (row) => row.status?.toLowerCase() === statusFilter.toLowerCase()
      );
    }

    if (calculationOnFilter) {
      filteredRows = filteredRows.filter(
        (row) => row.calculationOn?.toLowerCase() === calculationOnFilter.toLowerCase()
      );
    }

    return filteredRows;
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleTaxTypeFilterChange = (e: any) => {
    setTaxTypeFilter(e.target.value);
  };

  const handleStatusFilterChange = (e: any) => {
    setStatusFilter(e.target.value);
  };

  const handleCalculationOnFilterChange = (e: any) => {
    setCalculationOnFilter(e.target.value);
  };

  const handleCloseDialogs = () => {
    setOpenEditDialog(false);
    setOpenAddDialog(false);
    setEditedTax(null);
  };

  const editTax = (taxData: TaxData) => {
    setEditedTax(taxData);
    setOpenEditDialog(true);
  };

  const deleteTax = (id: number) => {
    setDeleteConfirmationId(id);
    setOpenDeleteConfirmation(true);
  };

  const performDelete = async (id: number) => {
    try {
      const response = await deleteEntry(`${process.env.REACT_APP_BASE_API_URL}/api/tax/delete/${id}`);
      if (response && (response.success || response === 'Successfully deleted')) {
        showSnackbar('Tax deleted successfully.', 'success');
        await fetchTaxData();
      } else {
        showSnackbar('Error deleting tax.', 'error');
      }
    } catch (error) {
      console.error('Error deleting tax:', error);
      showSnackbar('Error deleting tax.', 'error');
    } finally {
      setDeleteConfirmationId(null);
      setOpenDeleteConfirmation(false);
    }
  };

  const columns = [
    { field: 'id', headerName: 'ID', flex: 1 },
    { field: 'taxType', headerName: 'Tax Type', flex: 1 },
    { field: 'value', headerName: 'Value', flex: 1 },
    { 
      field: 'percentage', 
      headerName: 'Percentage', 
      flex: 1,
      renderCell: (params) => params.value ? 'Yes' : 'No'
    },
    { 
      field: 'addition', 
      headerName: 'Addition', 
      flex: 1,
      renderCell: (params) => params.value ? 'Yes' : 'No'
    },
    { field: 'calculationOn', headerName: 'Calc. On', flex: 1 },
    { field: 'calculationOrder', headerName: 'Order', flex: 1 },
    {
      field: 'createdOn',
      headerName: 'Created On',
      flex: 1,
      valueGetter: (params) => params.value ? dayjs(params.value).format('DD/MMM/YYYY') : '',
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      renderCell: (params) => (
        <span style={{ 
          color: params.value === 'Active' ? 'green' : params.value === 'Pending' ? 'orange' : 'red',
          fontWeight: 'bold'
        }}>
          {params.value}
        </span>
      )
    },
    {
      field: 'action',
      headerName: 'Action',
      flex: 1,
      renderCell: (params) => (
        <>
          {hasPermission(["OWNER", "tax.edit"]) && (
            <EditIcon
              style={{ cursor: 'pointer', marginRight: '8px', color: '#1976D2' }}
              onClick={() => editTax(params.row)}
            />
          )}
          {hasPermission(["OWNER", "tax.delete"]) && (
            <DeleteIcon
              style={{ cursor: 'pointer', color: '#E53935' }}
              onClick={() => deleteTax(params.row.id)}
            />
          )}
        </>
      ),
    }
  ];

  const [paginationModel, setPaginationModel] = useState({ pageSize: 10, page: 0 });

  return (
    <>
      <Helmet>
        <title>Manage Tax</title>
      </Helmet>

      {/* Add Tax Dialog */}
      {hasPermission(["OWNER", "tax.create"]) && (
        <Dialog
          open={openAddDialog}
          onClose={handleCloseDialogs}
          aria-labelledby="add-dialog-title"
          aria-describedby="add-dialog-description"
          maxWidth="md"
          fullWidth
        >
          <DialogTitle id="add-dialog-title">Add Tax</DialogTitle>
          <DialogContent>
            {openAddDialog && (
              <AddTax
                handleClose={handleCloseDialogs}
                fetchTaxData={fetchTaxData}
                showSnackbar={showSnackbar}
              />
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* Edit Tax Dialog */}
      {hasPermission(["OWNER", "tax.edit"]) && (
        <Dialog
          open={openEditDialog}
          onClose={handleCloseDialogs}
          aria-labelledby="edit-dialog-title"
          aria-describedby="edit-dialog-description"
          maxWidth="md"
          fullWidth
        >
          <DialogTitle id="edit-dialog-title">Edit Tax</DialogTitle>
          <DialogContent>
            {editedTax && (
              <EditTax
                taxData={editedTax}
                handleClose={handleCloseDialogs}
                handleRefresh={fetchTaxData}
                showSnackbar={showSnackbar}
              />
            )}
          </DialogContent>
        </Dialog>
      )}

      <PageTitleWrapper>
        {hasPermission(["OWNER", "tax.create"]) ? (
          <PageTitle
            heading="Manage Tax"
            subHeading="Manage tax rates and configurations."
            label="Add Tax"
            handleClick={handleOpenAddDialog}
          />
        ) : (
          <PageTitle
            heading="Tax List"
            subHeading="Manage tax rates and configurations."
            handleClick={handleOpenAddDialog}
          />
        )}
      </PageTitleWrapper>

      {/* Filters Section */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, width: '100%', padding: '0 15px', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Search"
          variant="outlined"
          value={searchQuery}
          onChange={handleSearch}
          size="small"
          sx={{ minWidth: '200px', flex: 1 }}
        />

        <FormControl variant="outlined" size="small" sx={{ minWidth: '180px', flex: 1 }}>
          <InputLabel>Tax Type Filter</InputLabel>
          <Select
            value={taxTypeFilter}
            onChange={handleTaxTypeFilterChange}
            label="Tax Type Filter"
          >
            <MenuItem value="">ALL</MenuItem>
            {taxTypeOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl variant="outlined" size="small" sx={{ minWidth: '180px', flex: 1 }}>
          <InputLabel>Status Filter</InputLabel>
          <Select
            value={statusFilter}
            onChange={handleStatusFilterChange}
            label="Status Filter"
          >
            <MenuItem value="">ALL</MenuItem>
            {statusOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl variant="outlined" size="small" sx={{ minWidth: '180px', flex: 1 }}>
          <InputLabel>Calc. On Filter</InputLabel>
          <Select
            value={calculationOnFilter}
            onChange={handleCalculationOnFilterChange}
            label="Calc. On Filter"
          >
            <MenuItem value="">ALL</MenuItem>
            {calculationOnOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Data Grid */}
      <Box sx={{ height: 'calc(100vh - 220px)', width: '100%', padding: '15px' }}>
        <DataGrid
          rows={getFilteredRows()}
          columns={columns}
          loading={loading}
          components={{ Toolbar: GridToolbar }}
          componentsProps={{
            toolbar: {
              showQuickFilter: true,
            },
          }}
          sx={{
            boxShadow: 2,
            border: 2,
            borderColor: 'rgb(43 44 45)',
            '& .MuiDataGrid-cell:hover': {
              color: 'rgb(43 44 45)',
            },
          }}
          pagination
          rowsPerPageOptions={[10, 20, 50]}
          pageSize={paginationModel.pageSize}
          onPageSizeChange={(newPageSize) => setPaginationModel((prev) => ({ ...prev, pageSize: newPageSize }))}
          page={paginationModel.page}
          onPageChange={(newPage) => setPaginationModel((prev) => ({ ...prev, page: newPage }))}
          autoHeight={false}
        />
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteConfirmation}
        onClose={() => setOpenDeleteConfirmation(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this tax?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteConfirmation(false)} color="primary">
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (deleteConfirmationId) {
                performDelete(deleteConfirmationId);
              }
            }}
            color="error"
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Tax;